import React, { useState } from 'react';
import { Clock, Sun, Moon, Coffee, Utensils, Target, Tag, Plus, Trash2, Edit2, Check, X, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Default anomalous reasons
const DEFAULT_ANOMALOUS_REASONS = [
  { id: 'breakdown', name: 'Avaria', color: '#ef4444', icon: '🔧' },
  { id: 'material', name: 'Falta de Material', color: '#f59e0b', icon: '📦' },
  { id: 'setup', name: 'Setup/Preparação', color: '#3b82f6', icon: '⚙️' },
  { id: 'maintenance', name: 'Manutenção Preventiva', color: '#8b5cf6', icon: '🛠️' },
  { id: 'operator', name: 'Ausência Operador', color: '#ec4899', icon: '👤' },
  { id: 'quality', name: 'Controlo Qualidade', color: '#06b6d4', icon: '✓' },
  { id: 'other', name: 'Outro', color: '#6b7280', icon: '📝' },
];

// Sortable Reason Item Component
function SortableReasonItem({ reason, editingReason, setEditingReason, updateReason, deleteReason, canDelete, t }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: reason.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${t.inner} rounded-lg p-3 flex items-center justify-between group ${isDragging ? 'ring-2 ring-amber-500' : ''}`}
    >
      {editingReason === reason.id ? (
        // Edit mode
        <div className="flex items-center gap-3 flex-1">
          <input
            type="text"
            value={reason.name}
            onChange={(e) => updateReason(reason.id, { name: e.target.value })}
            className={`flex-1 px-3 py-1.5 rounded-md text-sm ${t.inputBg} ${t.card} ${t.text}`}
          />
          <input
            type="color"
            value={reason.color}
            onChange={(e) => updateReason(reason.id, { color: e.target.value })}
            className="w-8 h-8 rounded cursor-pointer"
          />
          <input
            type="text"
            value={reason.icon}
            onChange={(e) => updateReason(reason.id, { icon: e.target.value })}
            maxLength={2}
            className={`w-10 px-2 py-1.5 rounded-md text-sm text-center ${t.inputBg} ${t.card} ${t.text}`}
          />
          <button
            onClick={() => setEditingReason(null)}
            className="p-1.5 rounded bg-emerald-500 text-white hover:bg-emerald-600"
          >
            <Check className="w-4 h-4" />
          </button>
        </div>
      ) : (
        // View mode
        <>
          <div className="flex items-center gap-3">
            {/* Drag Handle */}
            <button
              {...attributes}
              {...listeners}
              className={`p-1 rounded cursor-grab active:cursor-grabbing ${t.textMut} hover:text-amber-500`}
            >
              <GripVertical className="w-4 h-4" />
            </button>
            <span 
              className="w-8 h-8 rounded-lg flex items-center justify-center text-lg"
              style={{ backgroundColor: reason.color + '20' }}
            >
              {reason.icon}
            </span>
            <span className={`font-medium ${t.text}`}>{reason.name}</span>
            <span 
              className="w-4 h-4 rounded-full"
              style={{ backgroundColor: reason.color }}
            />
          </div>
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={() => setEditingReason(reason.id)}
              className={`p-1.5 rounded ${t.card} ${t.textMut} hover:text-blue-500`}
            >
              <Edit2 className="w-4 h-4" />
            </button>
            <button
              onClick={() => deleteReason(reason.id)}
              disabled={!canDelete}
              className={`p-1.5 rounded ${t.card} ${t.textMut} hover:text-red-500 disabled:opacity-30`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function SettingsPanel({ settings, onSave, t }) {
  const [local, setLocal] = useState(settings);
  const [anomalousReasons, setAnomalousReasons] = useState(
    settings.anomalousReasons || DEFAULT_ANOMALOUS_REASONS
  );
  const [editingReason, setEditingReason] = useState(null);
  const [newReason, setNewReason] = useState({ name: '', color: '#6b7280', icon: '📝' });
  const [showAddReason, setShowAddReason] = useState(false);
  
  const resetToDefaults = () => {
    setLocal({
      efficiencyTarget: 60,
      efficiencyWarning: 45,
      machineStoppedTarget: 25,
      machineStoppedWarning: 35,
      entreCorteTarget: 2,
      entreCorteWarning: 3,
      speedTarget: 11,
      speedWarning: 9,
      morningStart: "08:00",
      morningEnd: "12:00",
      afternoonStart: "13:30",
      afternoonEnd: "17:30",
      break1Start: "10:00",
      break2Start: "16:00",
      breakDuration: 10,
      allowWeekends: false,
    });
    setAnomalousReasons(DEFAULT_ANOMALOUS_REASONS);
  };
  
  // Save with reasons
  const handleSave = () => {
    onSave({ ...local, anomalousReasons });
  };
  
  // Add new reason
  const addReason = () => {
    if (!newReason.name.trim()) return;
    const id = newReason.name.toLowerCase().replace(/\s+/g, '_') + '_' + Date.now();
    setAnomalousReasons([...anomalousReasons, { ...newReason, id }]);
    setNewReason({ name: '', color: '#6b7280', icon: '📝' });
    setShowAddReason(false);
  };
  
  // Update reason
  const updateReason = (id, updates) => {
    setAnomalousReasons(anomalousReasons.map(r => r.id === id ? { ...r, ...updates } : r));
  };
  
  // Delete reason
  const deleteReason = (id) => {
    if (anomalousReasons.length <= 1) return; // Keep at least one
    setAnomalousReasons(anomalousReasons.filter(r => r.id !== id));
  };
  
  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  
  // Handle drag end
  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      setAnomalousReasons((items) => {
        const oldIndex = items.findIndex(i => i.id === active.id);
        const newIndex = items.findIndex(i => i.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };
  
  // Calculate useful time
  const parseTime = (timeStr) => {
    const [h, m] = (timeStr || "00:00").split(':').map(Number);
    return h * 60 + m;
  };
  const morningMin = parseTime(local.morningEnd) - parseTime(local.morningStart);
  const afternoonMin = parseTime(local.afternoonEnd) - parseTime(local.afternoonStart);
  const lunchMin = parseTime(local.afternoonStart) - parseTime(local.morningEnd);
  const breaksMin = (local.breakDuration || 10) * 2;
  const totalUsefulMin = morningMin + afternoonMin - breaksMin;
  const totalUsefulHours = Math.floor(totalUsefulMin / 60);
  const totalUsefulRemainMin = totalUsefulMin % 60;

  // Timeline calculations
  const timelineStart = 8 * 60; // 08:00
  const timelineEnd = 18 * 60;  // 18:00
  const timelineWidth = timelineEnd - timelineStart;

  const getPosition = (timeStr) => {
    const min = parseTime(timeStr);
    return ((min - timelineStart) / timelineWidth) * 100;
  };

  const getWidth = (startStr, endStr) => {
    return getPosition(endStr) - getPosition(startStr);
  };

  const hours = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18];

  return (
    <div className="space-y-6">
      {/* HORÁRIO DE FUNCIONAMENTO - Visual Timeline */}
      <div className={`${t.card} rounded-lg p-6`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-sky-500" />
            <h3 className={`text-sm font-semibold ${t.text} uppercase tracking-wider`} style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
              Horário de Funcionamento
            </h3>
          </div>
          <span className={`text-sm ${t.textSec}`}>
            Tempo Útil: <span className="font-semibold">{totalUsefulHours}h{totalUsefulRemainMin > 0 ? totalUsefulRemainMin : ''}</span> ({totalUsefulMin} min)
          </span>
        </div>

        {/* Time blocks */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className={`p-3 rounded-lg border ${t.inner}`}>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 rounded-md flex items-center justify-center border bg-sky-500/10 border-sky-500/20 text-sky-500">
                <Sun className="w-3.5 h-3.5" />
              </div>
              <span className={`text-[10px] uppercase tracking-wider font-medium ${t.textMut}`}>MANHÃ</span>
            </div>
            <div className={`text-base font-semibold ${t.text}`}>{local.morningStart} - {local.morningEnd}</div>
            <div className={`text-xs ${t.textMut}`}>{Math.floor(morningMin / 60)}h</div>
          </div>
          <div className={`p-3 rounded-lg border ${t.inner}`}>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 rounded-md flex items-center justify-center border bg-indigo-500/10 border-indigo-500/20 text-indigo-500">
                <Moon className="w-3.5 h-3.5" />
              </div>
              <span className={`text-[10px] uppercase tracking-wider font-medium ${t.textMut}`}>TARDE</span>
            </div>
            <div className={`text-base font-semibold ${t.text}`}>{local.afternoonStart} - {local.afternoonEnd}</div>
            <div className={`text-xs ${t.textMut}`}>{Math.floor(afternoonMin / 60)}h</div>
          </div>
          <div className={`p-3 rounded-lg border ${t.inner}`}>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 rounded-md flex items-center justify-center border bg-amber-500/10 border-amber-500/20 text-amber-500">
                <Coffee className="w-3.5 h-3.5" />
              </div>
              <span className={`text-[10px] uppercase tracking-wider font-medium ${t.textMut}`}>PAUSAS</span>
            </div>
            <div className={`text-base font-semibold ${t.text}`}>{local.break1Start} e {local.break2Start}</div>
            <div className={`text-xs ${t.textMut}`}>2 x {local.breakDuration} min</div>
          </div>
          <div className={`p-3 rounded-lg border ${t.inner}`}>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 rounded-md flex items-center justify-center border bg-gray-500/10 border-gray-500/20 text-gray-400">
                <Utensils className="w-3.5 h-3.5" />
              </div>
              <span className={`text-[10px] uppercase tracking-wider font-medium ${t.textMut}`}>ALMOÇO</span>
            </div>
            <div className={`text-base font-semibold ${t.text}`}>{local.morningEnd} - {local.afternoonStart}</div>
            <div className={`text-xs ${t.textMut}`}>{Math.floor(lunchMin / 60)}h{lunchMin % 60 > 0 ? (lunchMin % 60) + 'min' : ''}</div>
          </div>
        </div>

        {/* Visual Timeline */}
        <div className="relative mb-4">
          {/* Hour markers */}
          <div className="flex justify-between text-[10px] text-gray-400 mb-1">
            {hours.map(h => (
              <span key={h}>{h.toString().padStart(2, '0')}h</span>
            ))}
          </div>
          
          {/* Timeline bar */}
          <div className={`relative h-8 ${t.inner} rounded-full overflow-hidden`}>
            {/* Morning work */}
            <div 
              className="absolute h-full bg-sky-500 rounded-l-full flex items-center justify-center text-[10px] text-white font-medium"
              style={{ 
                left: `${getPosition(local.morningStart)}%`, 
                width: `${getWidth(local.morningStart, local.break1Start)}%` 
              }}
            >
              Trabalho
            </div>
            {/* Break 1 */}
            <div 
              className="absolute h-full bg-amber-500"
              style={{ 
                left: `${getPosition(local.break1Start)}%`, 
                width: `${(local.breakDuration / timelineWidth) * 100}%` 
              }}
            />
            {/* Morning work after break */}
            <div 
              className="absolute h-full bg-sky-500 flex items-center justify-center text-[10px] text-white font-medium"
              style={{ 
                left: `${getPosition(local.break1Start) + (local.breakDuration / timelineWidth) * 100}%`, 
                width: `${getWidth(local.break1Start, local.morningEnd) - (local.breakDuration / timelineWidth) * 100}%` 
              }}
            >
              Trabalho
            </div>
            {/* Lunch */}
            <div 
              className="absolute h-full bg-gray-400 flex items-center justify-center text-[10px] text-white font-medium"
              style={{ 
                left: `${getPosition(local.morningEnd)}%`, 
                width: `${getWidth(local.morningEnd, local.afternoonStart)}%` 
              }}
            >
              Almoço
            </div>
            {/* Afternoon work before break */}
            <div 
              className="absolute h-full bg-sky-500 flex items-center justify-center text-[10px] text-white font-medium"
              style={{ 
                left: `${getPosition(local.afternoonStart)}%`, 
                width: `${getWidth(local.afternoonStart, local.break2Start)}%` 
              }}
            >
              Trabalho
            </div>
            {/* Break 2 */}
            <div 
              className="absolute h-full bg-amber-500"
              style={{ 
                left: `${getPosition(local.break2Start)}%`, 
                width: `${(local.breakDuration / timelineWidth) * 100}%` 
              }}
            />
            {/* Afternoon work after break */}
            <div 
              className="absolute h-full bg-sky-500 rounded-r-full flex items-center justify-center text-[10px] text-white font-medium"
              style={{ 
                left: `${getPosition(local.break2Start) + (local.breakDuration / timelineWidth) * 100}%`, 
                width: `${getWidth(local.break2Start, local.afternoonEnd) - (local.breakDuration / timelineWidth) * 100}%` 
              }}
            >
              Trabalho
            </div>
          </div>
          
          {/* Legend */}
          <div className="flex items-center justify-center gap-6 mt-3">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-sky-500 rounded-full" />
              <span className={`text-xs ${t.textMut}`}>Trabalho ({totalUsefulHours}h{totalUsefulRemainMin > 0 ? totalUsefulRemainMin : ''})</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-amber-500 rounded-full" />
              <span className={`text-xs ${t.textMut}`}>Pausas ({breaksMin} min)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-gray-400 rounded-full" />
              <span className={`text-xs ${t.textMut}`}>Almoço ({Math.floor(lunchMin / 60)}h{lunchMin % 60 > 0 ? (lunchMin % 60) : ''})</span>
            </div>
          </div>
        </div>

        {/* Time inputs grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          {/* Manhã */}
          <div>
            <label className={`text-sm ${t.textLabel} mb-2 block`}>Manhã Início</label>
            <input
              type="time"
              value={local.morningStart}
              onChange={(e) => setLocal({...local, morningStart: e.target.value})}
              className={`w-full px-3 py-2 rounded-md text-sm ${t.inputBg} ${t.card} ${t.text}`}
            />
          </div>
          <div>
            <label className={`text-sm ${t.textLabel} mb-2 block`}>Manhã Fim</label>
            <input
              type="time"
              value={local.morningEnd}
              onChange={(e) => setLocal({...local, morningEnd: e.target.value})}
              className={`w-full px-3 py-2 rounded-md text-sm ${t.inputBg} ${t.card} ${t.text}`}
            />
          </div>
          
          {/* Tarde */}
          <div>
            <label className={`text-sm ${t.textLabel} mb-2 block`}>Tarde Início</label>
            <input
              type="time"
              value={local.afternoonStart}
              onChange={(e) => setLocal({...local, afternoonStart: e.target.value})}
              className={`w-full px-3 py-2 rounded-md text-sm ${t.inputBg} ${t.card} ${t.text}`}
            />
          </div>
          <div>
            <label className={`text-sm ${t.textLabel} mb-2 block`}>Tarde Fim</label>
            <input
              type="time"
              value={local.afternoonEnd}
              onChange={(e) => setLocal({...local, afternoonEnd: e.target.value})}
              className={`w-full px-3 py-2 rounded-md text-sm ${t.inputBg} ${t.card} ${t.text}`}
            />
          </div>
          
          {/* Pausas */}
          <div>
            <label className={`text-sm ${t.textLabel} mb-2 block`}>Pausa Manhã</label>
            <input
              type="time"
              value={local.break1Start}
              onChange={(e) => setLocal({...local, break1Start: e.target.value})}
              className={`w-full px-3 py-2 rounded-md text-sm ${t.inputBg} ${t.card} ${t.text}`}
            />
          </div>
          <div>
            <label className={`text-sm ${t.textLabel} mb-2 block`}>Pausa Tarde</label>
            <input
              type="time"
              value={local.break2Start}
              onChange={(e) => setLocal({...local, break2Start: e.target.value})}
              className={`w-full px-3 py-2 rounded-md text-sm ${t.inputBg} ${t.card} ${t.text}`}
            />
          </div>
          <div>
            <label className={`text-sm ${t.textLabel} mb-2 block`}>Duração Pausa (min)</label>
            <input
              type="number"
              value={local.breakDuration}
              onChange={(e) => setLocal({...local, breakDuration: parseInt(e.target.value) || 10})}
              className={`w-full px-3 py-2 rounded-md text-sm ${t.inputBg} ${t.card} ${t.text}`}
            />
          </div>
        </div>
      </div>

      {/* METAS DE DESEMPENHO */}
      <div className={`${t.card} rounded-lg p-6`}>
        <div className="flex items-center gap-2 mb-4">
          <Target className="w-5 h-5 text-emerald-500" />
          <h3 className={`text-sm font-semibold ${t.text} uppercase tracking-wider`} style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
            Metas de Desempenho
          </h3>
        </div>
        
        <p className={`text-xs ${t.textMut} mb-4`}>
          Defina os valores que determinam as cores (verde/amarelo/vermelho) na análise diária
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {/* Eficiência */}
          <div className={`p-4 rounded-lg border ${t.inner} border-emerald-500/30`}>
            <label className={`text-sm ${t.textLabel} mb-2 block font-medium text-emerald-400`}>Eficiência Mínima</label>
            <div className="flex items-center gap-2 mb-2">
              <input
                type="number"
                min="0"
                max="100"
                step="1"
                value={local.efficiencyTarget || 35}
                onChange={(e) => setLocal({...local, efficiencyTarget: parseInt(e.target.value) || 35})}
                className={`w-20 px-3 py-2 rounded-md text-sm text-center ${t.inputBg} ${t.card} ${t.text}`}
              />
              <span className={`text-sm ${t.textMut}`}>%</span>
            </div>
            <p className={`text-xs ${t.textMut}`}>
              <span className="text-emerald-400">●</span> Verde se ≥ este valor
            </p>
            <div className="flex items-center gap-2 mt-2">
              <span className={`text-xs ${t.textMut}`}>Amarelo se ≥</span>
              <input
                type="number"
                min="0"
                max="100"
                step="1"
                value={local.efficiencyWarning || 25}
                onChange={(e) => setLocal({...local, efficiencyWarning: parseInt(e.target.value) || 25})}
                className={`w-16 px-2 py-1 rounded text-xs text-center ${t.inputBg} ${t.card} ${t.text}`}
              />
              <span className={`text-xs ${t.textMut}`}>%</span>
            </div>
          </div>
          
          {/* % Parada */}
          <div className={`p-4 rounded-lg border ${t.inner} border-red-500/30`}>
            <label className={`text-sm ${t.textLabel} mb-2 block font-medium text-red-400`}>% Parada Máx</label>
            <div className="flex items-center gap-2 mb-2">
              <input
                type="number"
                min="0"
                max="100"
                step="1"
                value={local.machineStoppedTarget || 25}
                onChange={(e) => setLocal({...local, machineStoppedTarget: parseInt(e.target.value) || 25})}
                className={`w-20 px-3 py-2 rounded-md text-sm text-center ${t.inputBg} ${t.card} ${t.text}`}
              />
              <span className={`text-sm ${t.textMut}`}>%</span>
            </div>
            <p className={`text-xs ${t.textMut}`}>
              <span className="text-emerald-400">●</span> Verde se ≤ este valor
            </p>
            <div className="flex items-center gap-2 mt-2">
              <span className={`text-xs ${t.textMut}`}>Amarelo se ≤</span>
              <input
                type="number"
                min="0"
                max="100"
                step="1"
                value={local.machineStoppedWarning || 35}
                onChange={(e) => setLocal({...local, machineStoppedWarning: parseInt(e.target.value) || 35})}
                className={`w-16 px-2 py-1 rounded text-xs text-center ${t.inputBg} ${t.card} ${t.text}`}
              />
              <span className={`text-xs ${t.textMut}`}>%</span>
            </div>
          </div>
          
          {/* Tempo Entre */}
          <div className={`p-4 rounded-lg border ${t.inner} border-cyan-500/30`}>
            <label className={`text-sm ${t.textLabel} mb-2 block font-medium text-cyan-400`}>Tempo E.Marc Máx</label>
            <div className="flex items-center gap-2 mb-2">
              <input
                type="number"
                min="0"
                max="30"
                step="0.1"
                value={local.entreCorteTarget || 2}
                onChange={(e) => setLocal({...local, entreCorteTarget: parseFloat(e.target.value) || 2})}
                className={`w-20 px-3 py-2 rounded-md text-sm text-center ${t.inputBg} ${t.card} ${t.text}`}
              />
              <span className={`text-sm ${t.textMut}`}>min</span>
            </div>
            <p className={`text-xs ${t.textMut}`}>
              <span className="text-emerald-400">●</span> Verde se ≤ este valor
            </p>
            <div className="flex items-center gap-2 mt-2">
              <span className={`text-xs ${t.textMut}`}>Amarelo se ≤</span>
              <input
                type="number"
                min="0"
                max="30"
                step="0.1"
                value={local.entreCorteWarning || 3}
                onChange={(e) => setLocal({...local, entreCorteWarning: parseFloat(e.target.value) || 3})}
                className={`w-16 px-2 py-1 rounded text-xs text-center ${t.inputBg} ${t.card} ${t.text}`}
              />
              <span className={`text-xs ${t.textMut}`}>min</span>
            </div>
          </div>
          
          {/* Velocidade */}
          <div className={`p-4 rounded-lg border ${t.inner} border-sky-500/30`}>
            <label className={`text-sm ${t.textLabel} mb-2 block font-medium text-sky-400`}>Velocidade Mínima</label>
            <div className="flex items-center gap-2 mb-2">
              <input
                type="number"
                min="0"
                max="50"
                step="0.5"
                value={local.speedTarget || 10}
                onChange={(e) => setLocal({...local, speedTarget: parseFloat(e.target.value) || 10})}
                className={`w-20 px-3 py-2 rounded-md text-sm text-center ${t.inputBg} ${t.card} ${t.text}`}
              />
              <span className={`text-sm ${t.textMut}`}>m/min</span>
            </div>
            <p className={`text-xs ${t.textMut}`}>
              <span className="text-emerald-400">●</span> Verde se ≥ este valor
            </p>
            <div className="flex items-center gap-2 mt-2">
              <span className={`text-xs ${t.textMut}`}>Amarelo se ≥</span>
              <input
                type="number"
                min="0"
                max="50"
                step="0.5"
                value={local.speedWarning || 8}
                onChange={(e) => setLocal({...local, speedWarning: parseFloat(e.target.value) || 8})}
                className={`w-16 px-2 py-1 rounded text-xs text-center ${t.inputBg} ${t.card} ${t.text}`}
              />
              <span className={`text-xs ${t.textMut}`}>m/min</span>
            </div>
          </div>
        </div>
        
        {/* Color Legend */}
        <div className={`${t.inner} rounded-lg p-3 flex items-center gap-6 text-xs`}>
          <span className={t.textMut}>Legenda:</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-emerald-500"></span> Dentro do objetivo</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-amber-500"></span> Perto do limite</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-red-500"></span> Fora do objetivo</span>
        </div>
      </div>

      {/* GESTÃO DE MOTIVOS DE PARAGEM */}
      <div className={`${t.card} rounded-lg p-6`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Tag className="w-5 h-5 text-amber-500" />
            <h3 className={`text-sm font-semibold ${t.text} uppercase tracking-wider`} style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
              Motivos de Paragem
            </h3>
          </div>
          <Button 
            onClick={() => setShowAddReason(true)} 
            size="sm" 
            className="bg-amber-500 hover:bg-amber-600 text-white"
          >
            <Plus className="w-4 h-4 mr-1" />
            Adicionar
          </Button>
        </div>
        
        <p className={`text-xs ${t.textMut} mb-4`}>
          Configure os motivos disponíveis para classificar tempos anormais durante a importação.
        </p>

        {/* Add New Reason Form */}
        {showAddReason && (
          <div className={`${t.inner} rounded-lg p-4 mb-4 border border-amber-500/30`}>
            <h4 className={`text-sm font-medium ${t.text} mb-3`}>Novo Motivo</h4>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label className={`text-xs ${t.textMut} block mb-1`}>Nome</label>
                <input
                  type="text"
                  value={newReason.name}
                  onChange={(e) => setNewReason({...newReason, name: e.target.value})}
                  placeholder="Ex: Avaria Eléctrica"
                  className={`w-full px-3 py-2 rounded-md text-sm ${t.inputBg} ${t.card} ${t.text}`}
                />
              </div>
              <div>
                <label className={`text-xs ${t.textMut} block mb-1`}>Cor</label>
                <input
                  type="color"
                  value={newReason.color}
                  onChange={(e) => setNewReason({...newReason, color: e.target.value})}
                  className="w-10 h-10 rounded cursor-pointer"
                />
              </div>
              <div>
                <label className={`text-xs ${t.textMut} block mb-1`}>Ícone</label>
                <input
                  type="text"
                  value={newReason.icon}
                  onChange={(e) => setNewReason({...newReason, icon: e.target.value})}
                  maxLength={2}
                  className={`w-12 px-2 py-2 rounded-md text-sm text-center ${t.inputBg} ${t.card} ${t.text}`}
                />
              </div>
              <button
                onClick={addReason}
                disabled={!newReason.name.trim()}
                className="px-3 py-2 rounded-md bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50"
              >
                <Check className="w-4 h-4" />
              </button>
              <button
                onClick={() => {
                  setShowAddReason(false);
                  setNewReason({ name: '', color: '#6b7280', icon: '📝' });
                }}
                className={`px-3 py-2 rounded-md ${t.inner} ${t.textMut} hover:opacity-80`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Reasons List with Drag and Drop */}
        <DndContext 
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext 
            items={anomalousReasons.map(r => r.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2">
              {anomalousReasons.map((reason) => (
                <SortableReasonItem
                  key={reason.id}
                  reason={reason}
                  editingReason={editingReason}
                  setEditingReason={setEditingReason}
                  updateReason={updateReason}
                  deleteReason={deleteReason}
                  canDelete={anomalousReasons.length > 1}
                  t={t}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
        
        {/* Reset to defaults */}
        <button
          onClick={() => setAnomalousReasons(DEFAULT_ANOMALOUS_REASONS)}
          className={`mt-4 text-xs ${t.textMut} hover:text-amber-500 transition-colors`}
        >
          Repor motivos predefinidos
        </button>
      </div>

      {/* Buttons */}
      <div className="flex gap-3">
        <button 
          onClick={resetToDefaults}
          className={`px-4 py-2 text-sm rounded-md ${t.card} ${t.textSec} hover:opacity-80 transition-opacity`}
        >
          Repor Predefinições
        </button>
        <button 
          onClick={handleSave}
          className="px-4 py-2 text-sm rounded-md bg-sky-500 text-white hover:bg-sky-600 transition-colors"
        >
          Guardar Configurações
        </button>
      </div>
    </div>
  );
}

export default SettingsPanel;
